import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Page, ProductReference } from "@/features/comercial/tipos/desplegables";
import type { OrdersOverview } from "@/features/pedidos/types";
import { apiGet, apiPost } from "@/lib/api";

import type {
  CostTarget,
  FinishedApplyVariant,
  FinishedCorrection,
  FinishedCostOptions,
  FinishedLinkOrder,
  FinishedLoss,
  FinishedMovementIn,
  FinishedMovementList,
  FinishedPriceIn,
  FinishedPriceList,
  FinishedRelease,
  FinishedStockList,
  Quality,
  ResaleActivate,
  ResaleList,
  ResaleMaterialList,
  ResaleProduct,
  ResaleTransferIn,
  WarehouseValuation,
} from "./productTypes";

/**
 * La Bodega de producto. El saldo es la suma de movimientos y lo da el servidor; el valor (a
 * costo y a precio de venta), la merma y el total de las bodegas, también. La pantalla no
 * calcula dinero ni convierte unidades.
 */
const base = "/finished-stock";
const prefijo = ["finished-stock"] as const;

export type ProductStockParams = { quality?: Quality; q?: string; includeEmpty?: boolean };

/** Existencias por referencia y calidad. Sin `include_empty` el servidor solo da lo que tiene existencia. */
export function useProductStock(params: ProductStockParams) {
  const s = new URLSearchParams();
  if (params.quality) s.set("quality", params.quality);
  if (params.q) s.set("q", params.q);
  if (params.includeEmpty) s.set("include_empty", "true");
  return useQuery({
    queryKey: [...prefijo, "stock", params],
    queryFn: () => apiGet<FinishedStockList>(`${base}?${s.toString()}`),
    placeholderData: keepPreviousData,
  });
}

/** Lo que hay de una referencia en una calidad, por color o receta (sin color incluido). */
export function useProductStockOf(referenceId: string | undefined, quality: Quality, enabled = true) {
  return useQuery({
    queryKey: [...prefijo, "stock-of", referenceId ?? "", quality],
    queryFn: () =>
      apiGet<FinishedStockList>(`${base}?${new URLSearchParams({ reference_id: referenceId ?? "", quality })}`),
    enabled: enabled && Boolean(referenceId),
  });
}

/** El mismo saldo abierto por pedido de origen (`order_id` nulo = sin pedido). */
export function useProductByOrder(enabled: boolean, referenceId?: string, quality?: Quality) {
  const s = new URLSearchParams();
  if (referenceId) s.set("reference_id", referenceId);
  if (quality) s.set("quality", quality);
  return useQuery({
    queryKey: [...prefijo, "by-order", referenceId ?? "", quality ?? ""],
    queryFn: () => apiGet<FinishedStockList>(`${base}/by-order?${s.toString()}`),
    enabled,
  });
}

/** Los movimientos, con los anulados: nada se borra y la historia los conserva. */
export function useProductMovements(referenceId?: string, quality?: Quality, limit = 50) {
  const s = new URLSearchParams({ limit: String(limit) });
  if (referenceId) s.set("reference_id", referenceId);
  if (quality) s.set("quality", quality);
  return useQuery({
    queryKey: [...prefijo, "movements", referenceId ?? "", quality ?? "", limit],
    queryFn: () => apiGet<FinishedMovementList>(`${base}/movements?${s.toString()}`),
  });
}

/** La merma (entradas de tercera) con sus tres agrupaciones: por mes, referencia y pedido. */
export function useProductLoss() {
  return useQuery({
    queryKey: [...prefijo, "loss"],
    queryFn: () => apiGet<FinishedLoss>(`${base}/loss`),
  });
}

/** El color o la receta, en la búsqueda: el que no viene no se manda. */
function targetParams(s: URLSearchParams, target: CostTarget | null | undefined) {
  if (target?.color_id) s.set("color_id", target.color_id);
  if (target?.family_variant_id) s.set("family_variant_id", target.family_variant_id);
  return s;
}

/** La historia del precio de una referencia en una calidad y un color o receta, la vigente primero. */
export function useProductPrices(referenceId: string, quality: Quality, target: CostTarget) {
  return useQuery({
    queryKey: [...prefijo, "prices", referenceId, quality, target.color_id ?? "", target.family_variant_id ?? ""],
    queryFn: () =>
      apiGet<FinishedPriceList>(
        `${base}/prices/history?${targetParams(new URLSearchParams({ reference_id: referenceId, quality }), target)}`,
      ),
  });
}

/**
 * Los colores de paleta o las recetas que admite una referencia, cada uno con su costo por pieza
 * (y por m²) del costeo de hoy (`pick`: color · variant · none). **Informativo**: no descuenta nada.
 */
export function useProductCostOptions(referenceId: string | undefined) {
  return useQuery({
    queryKey: [...prefijo, "cost-options", referenceId ?? ""],
    queryFn: () =>
      apiGet<FinishedCostOptions>(`${base}/cost-options?${new URLSearchParams({ reference_id: referenceId ?? "" })}`),
    enabled: Boolean(referenceId),
    staleTime: 60_000,
  });
}

/**
 * Pedidos por el código de la cotización de Siigo (C-1-NNNN, ya importada), por el del pedido
 * (PD-…) o por cliente u obra; también los archivados: el sobrante suele ser de uno entregado.
 */
export function useOrderSearch(q: string) {
  return useQuery({
    queryKey: ["orders-overview", "finished-stock", q],
    queryFn: () =>
      apiGet<OrdersOverview>(`/orders/overview?${new URLSearchParams({ q, archive: "all", limit: "10" })}`),
    enabled: q.length >= 2,
    placeholderData: keepPreviousData,
  });
}

/** Un ítem del pedido, como lo da `GET /order-lines` (solo lo que esta pantalla lee). */
export type OrderLineOption = { id: string; pieces: string; note: string | null; sort_order: number };

/**
 * Los ítems de un pedido con esa referencia (y ese color de paleta, si lo tiene): «Liberar a la
 * venta» libera lo apartado **de un ítem** (`order_line_id`), y la fila de la Bodega dice el pedido,
 * no el ítem. Si el pedido tiene uno solo, es ese; si tiene varios, quien libera escoge.
 */
export function useOrderLinesOf(row: { order_id: string | null; reference_id: string; color_id: string | null } | null) {
  const s = new URLSearchParams({ order_id: row?.order_id ?? "", reference_id: row?.reference_id ?? "", limit: "50" });
  if (row?.color_id) s.set("color_id", row.color_id);
  return useQuery({
    queryKey: ["order-lines", "finished-stock", s.toString()],
    queryFn: () => apiGet<Page<OrderLineOption>>(`/order-lines?${s.toString()}`),
    enabled: Boolean(row?.order_id),
  });
}

/** Cada bodega que la persona alcanza, con su valor a costo, y el total. */
export function useWarehouseValues() {
  return useQuery({
    queryKey: [...prefijo, "valuation"],
    queryFn: () => apiGet<WarehouseValuation>("/stock/valuation"),
  });
}

/** Solo referencias de producto activas: una `non_production` no entra a la bodega. */
export function useProductReferenceOptions(q: string) {
  return useQuery({
    queryKey: ["product-references", "finished-stock", q],
    queryFn: () => {
      const s = new URLSearchParams({ kind: "product", is_active: "true", limit: "30" });
      if (q) s.set("q", q);
      return apiGet<Page<ProductReference>>(`/product-references?${s.toString()}`);
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * Todo lo que escribe en la bodega de producto refresca sus vistas y el valor de las bodegas.
 * Nada de esto entra al Deshacer global: un movimiento no se borra, se anula o se corrige.
 */
function useProductCommand<B, R = unknown>(run: (body: B) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => client.invalidateQueries({ queryKey: prefijo }),
  });
}

export const useCreateProductMovement = () =>
  useProductCommand((body: FinishedMovementIn) => apiPost(`${base}/movements`, body));

/** «Editar» = el servidor anula el movimiento con el motivo y anota el bueno, en una transacción. */
export const useCorrectProductMovement = () =>
  useProductCommand(({ id, ...body }: FinishedCorrection & { id: string }) =>
    apiPost(`${base}/movements/${id}/correct`, body),
  );

export const useVoidProductMovement = () =>
  useProductCommand(({ id, reason }: { id: string; reason: string }) =>
    apiPost(`${base}/movements/${id}/void`, { reason }),
  );

/**
 * «Aplicar color» o «Aplicar receta»: el servidor pasa la cantidad de «sin color» al color o la
 * receta en dos ajustes ligados; el total no cambia. «Vincular a un pedido», igual con el pedido.
 */
export const useApplyProductVariant = () =>
  useProductCommand((body: FinishedApplyVariant) => apiPost(`${base}/apply-variant`, body));

export const useLinkProductOrder = () =>
  useProductCommand((body: FinishedLinkOrder) => apiPost(`${base}/link-order`, body));

/**
 * «Liberar a la venta»: lo apartado de un ítem pasa a disponible con su motivo, en dos ajustes
 * ligados (la inversa de vincular). El pedido queda debiendo esas piezas; eso lo cuenta el servidor.
 * Refresca también los pedidos: su «listo para entregar» baja.
 */
export function useReleaseProduct() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: FinishedRelease) => apiPost(`${base}/release`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: prefijo });
      void client.invalidateQueries({ queryKey: ["orders-overview"] });
      void client.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export const useSetProductPrice = () =>
  useProductCommand((body: FinishedPriceIn) => apiPost(`${base}/prices`, body));

/**
 * La reventa (0145): lo que se compra y se vende tal cual, con su material, lo que hay en la
 * Principal y en la de producto, su costo por pieza y el último precio cotizado. La lee también el
 * buscador «Añadir ítem» del Cotizador (`enabled` solo con el buscador abierto).
 */
export function useResaleProducts(enabled = true) {
  return useQuery({
    queryKey: [...prefijo, "resale"],
    queryFn: () => apiGet<ResaleList>(`${base}/resale`),
    enabled,
  });
}

/**
 * Los materiales con existencia en cualquier bodega de materia prima (Principal, Cerámicos…), por
 * bodega, para cotizarlos desde «Bodega de productos» (3-nov). Lo lee también quien solo cotiza.
 */
export function useResaleMaterials() {
  return useQuery({
    queryKey: [...prefijo, "resale", "materials"],
    queryFn: () => apiGet<ResaleMaterialList>(`${base}/resale/materials`),
  });
}

/** «Activar para venta»: ata el material a su referencia de producto sin formato (o la crea). */
export const useActivateResale = () =>
  useProductCommand((body: ResaleActivate) => apiPost<ResaleProduct>(`${base}/resale`, body));

/**
 * Al cotizar un material aún sin habilitar, el sistema lo habilita solo (owner, 3-nov): basta poder
 * guardar cotizaciones, sin «Mover la bodega». Queda registrado con quien cotiza.
 */
export const useEnableResaleForQuote = () =>
  useProductCommand((raw_material_id: string) =>
    apiPost<ResaleProduct>(`${base}/resale/materials/${raw_material_id}/enable`, {}),
  );

/**
 * «Pasar a la Bodega de producto»: el servidor saca de la Principal y entra a la de producto en una
 * transacción. Refresca también la bodega de materia prima, que cambió de saldo.
 */
export function useTransferResale() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ reference_id, ...body }: ResaleTransferIn & { reference_id: string }) =>
      apiPost(`${base}/resale/${reference_id}/transfer`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: prefijo });
      void client.invalidateQueries({ queryKey: ["stock"] });
    },
  });
}
